import React, { useState, useEffect, useMemo } from 'react';
import Terminal, { ColorMode, TerminalOutput } from 'react-terminal-ui';
import { Button } from'./components/ui/button';
import { Card } from'./components/ui/card';
import { HamburgerMenuIcon, CodeIcon, EnvelopeClosedIcon, LinkedInLogoIcon } from"@radix-ui/react-icons";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from"@tsparticles/slim";
import'./App.css';

const App = () => {
    const [terminalLineData, setTerminalLineData] = useState([
        <TerminalOutput>Welcome to Qasam Ahmad's interactive CV!</TerminalOutput>,
        <TerminalOutput>Type "help" to see available commands.</TerminalOutput>
    ]);

    const [view, setView] = useState('detailed');
    const [tab, setTab] = useState('about');
    const [menuOpen, setMenuOpen] = useState(false);
    const [terminalHeight, setTerminalHeight] = useState("calc(100vh - 60px)");
    const [init, setInit] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const topBar = document.querySelector(".top-bar");
            if (window.scrollY > 50) {
                topBar.classList.add("hidden-top-bar");
            } else {
                topBar.classList.remove("hidden-top-bar");
            }
        };

        window.addEventListener("scroll", handleScroll);

        return() => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const handleFocus = () => {
            if (window.innerWidth <= 768) {
                setTerminalHeight("50vh");
            } else {
                const terminalWrapper = document.querySelector(".terminal-wrapper");
                const terminalInput = document.querySelector(".react-terminal-ui input");
    
                if (terminalWrapper && terminalInput) {
                    // Scroll to the bottom to ensure the input is visible without moving content out of view
                    terminalWrapper.scrollTop = terminalWrapper.scrollHeight;
                }
            }
        };
    
        const handleBlur = () => {
            setTerminalHeight("calc(100vh - 60px)");
        };
    
        const terminalInput = document.querySelector(".react-terminal-ui input");
        if (terminalInput) {
            terminalInput.addEventListener("focus", handleFocus);
            terminalInput.addEventListener("blur", handleBlur);
            terminalInput.setAttribute("autocapitalize", "none"); // Prevent auto-capitalization
        }
    
        return() => {
            if (terminalInput) {
                terminalInput.removeEventListener("focus", handleFocus);
                terminalInput.removeEventListener("blur", handleBlur);
            }
        };
    }, [terminalLineData]);        

    useEffect(() => {
        // Injecting style to override the white-space rule
        const style = document.createElement('style');
        style.innerHTML = `
            .react-terminal-line {
                white-space: normal !important;
            }
        `;
        document.head.appendChild(style);
        return() => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const terminalElement = document.querySelector('.terminal-wrapper');
        if (terminalElement) {
            terminalElement.scrollTop = 0;
        }
    }, [view]);

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await loadSlim(engine);
        }).then(() => {
            setInit(true);
        });
    }, []);

    const handleInput = (terminalInput) => {
        terminalInput = terminalInput.trim();
        let newLines = [];
        switch (terminalInput.toLowerCase()) {
            case'help':
                newLines = [
                    <TerminalOutput>Available commands:</TerminalOutput>,
                    <TerminalOutput>"experience" - View my work experience</TerminalOutput>,
                    <TerminalOutput>"skills" - See my technical skills</TerminalOutput>,
                    <TerminalOutput>"education" - Check my educational background</TerminalOutput>,
                    <TerminalOutput>"contact" - Get in touch with me</TerminalOutput>,
                    <TerminalOutput>"clear" - Clear the terminal</TerminalOutput>,
                ];
                break;
            case'experience':
                newLines = [
                    <TerminalOutput>Amazon Web Services (AWS) - Software Development Engineer (May 2024 - Present)</TerminalOutput>,
                    <TerminalOutput>• Worked within AWS ElastiCache, under in-memory databases, adding features and fixes to ElastiCache, DocumentDB, MemoryDB, and Timestream consoles across global regions.</TerminalOutput>,
                    <TerminalOutput>• Developed with React, TypeScript, JavaScript, and AWS, while carrying out DevOps and infrastructure improvements.</TerminalOutput>,
                    <TerminalOutput>Expedia Group - Software Engineer (Aug 2023 - May 2024)</TerminalOutput>,
                    <TerminalOutput>• Led the creation of a novel iOS widget in Swift and SwiftUI, using generative AI to make personalized recommendations based on users’ travel data.</TerminalOutput>,
                    <TerminalOutput>• Developed key front-end components for a UI library in Kotlin and Jetpack Compose, enhancing accessibility and ensuring UI consistency across platforms.</TerminalOutput>,
                    <TerminalOutput>Amazon (Alexa) - Software Development Engineer Intern (Sep 2022 - Nov 2022)</TerminalOutput>,
                    <TerminalOutput>• Developed and tested a voice-driven Alexa Skill using NodeJS and AWS Lambda, focusing on improving user experience by enabling link sharing features.</TerminalOutput>,
                    <TerminalOutput>• Collaborated effectively with senior engineers and UX designers to integrate and refine the skill, incorporating user feedback to enhance functionality.</TerminalOutput>,
                    <TerminalOutput>TikTok - Machine Learning Engineer Intern (Jun 2022 - Sep 2022)</TerminalOutput>,
                    <TerminalOutput>• Developed and tested a new age-sharing model for content recommendation, using TensorFlow and Python, resulting in a 1.031% increase in user re-engagement.</TerminalOutput>,
                    <TerminalOutput>• Executed comprehensive data analysis with efficient SQL queries and model optimization tasks, leading to a reduction in guideline violations per video view.</TerminalOutput>,
                    <TerminalOutput>Barclays - Technology Developer Intern (Jun 2021 - Aug 2021)</TerminalOutput>,
                    <TerminalOutput>• Engineered a Python-based document classification system leveraging AWS services (S3, Textract), achieving a reduction in manual resource needs and faster processing times.</TerminalOutput>,
                    <TerminalOutput>• Crafted an account opening process to reduce admin errors, showcasing proficiency in system automation and process optimization.</TerminalOutput>,
                ];
                break;
            case'skills':
                newLines = [
                    <TerminalOutput>Languages/Tools: Kotlin, Swift, Python, C#, SQL, JavaScript, AWS, NodeJS, Ansible, Git, React, TypeScript</TerminalOutput>,
                    <TerminalOutput>Frameworks: Jetpack Compose, SwiftUI, ASP.NET Core, Laravel</TerminalOutput>,
                ];
                break;
            case'education':
                newLines = [
                    <TerminalOutput>Georgia Institute of Technology - MS Computer Science (Jan 2024 - Present)</TerminalOutput>,
                    <TerminalOutput>• Modules: Machine Learning for Trading, Operating Systems</TerminalOutput>,
                    <TerminalOutput>Durham University - BSc Mathematics (Completed Jun 2023)</TerminalOutput>,
                ];
                break;
            case'contact':
                newLines = [
                    <TerminalOutput>Email: qasam.work@gmail.com</TerminalOutput>,
                    <TerminalOutput>LinkedIn: https://www.linkedin.com/in/qa1/</TerminalOutput>,
                ];
                break;
            case'clear':
                setTerminalLineData([]);
                return;
            default:
                newLines = [
                    <TerminalOutput>{terminalInput}</TerminalOutput>
                ];
                break;
        }
        setTerminalLineData([
            ...terminalLineData,
            ...newLines,
        ]);
    };

    const renderContent = () => {
        if (tab === 'experience') {
            return (
                <div><Card className="padded-card"><h2 className="text-lg font-bold">Amazon Web Services (AWS)</h2><p>Software Development Engineer (May 2024 - Present)</p><ul><li>• Worked within AWS ElastiCache, under in-memory databases, adding features and fixes to ElastiCache, DocumentDB, MemoryDB, and Timestream consoles across global regions.</li><li>• Developed with React, TypeScript, JavaScript, and AWS, while carrying out DevOps and infrastructure improvements.</li></ul></Card><Card className="padded-card"><h2 className="text-lg font-bold">Expedia Group</h2><p>Software Engineer (Aug 2023 - May 2024)</p><ul><li>• Led the creation of a novel iOS widget in Swift and SwiftUI, using generative AI to make personalized recommendations based on users’ travel data.</li><li>• Developed key front-end components for a UI library in Kotlin and Jetpack Compose, enhancing accessibility and ensuring UI consistency across platforms.</li></ul></Card><Card className="padded-card"><h2 className="text-lg font-bold">Amazon (Alexa)</h2><p>Software Development Engineer Intern (Sep 2022 - Nov 2022)</p><ul><li>• Developed and tested a voice-driven Alexa Skill using NodeJS and AWS Lambda, focusing on improving user experience by enabling link sharing features.</li><li>• Collaborated effectively with senior engineers and UX designers to integrate and refine the skill, incorporating user feedback to enhance functionality.</li></ul></Card><Card className="padded-card"><h2 className="text-lg font-bold">TikTok</h2><p>Machine Learning Engineer Intern (Jun 2022 - Sep 2022)</p><ul><li>• Developed and tested a new age-sharing model for content recommendation, using TensorFlow and Python, resulting in a 1.031% increase in user re-engagement.</li><li>• Executed comprehensive data analysis with efficient SQL queries and model optimization tasks, leading to a reduction in guideline violations per video view.</li></ul></Card><Card className="padded-card"><h2 className="text-lg font-bold">Barclays</h2><p>Technology Developer Intern (Jun 2021 - Aug 2021)</p><ul><li>• Engineered a Python-based document classification system leveraging AWS services (S3, Textract), achieving a reduction in manual resource needs and faster processing times.</li><li>• Crafted an account opening process to reduce admin errors, showcasing proficiency in system automation and process optimization.</li></ul></Card></div>
            );
        }
        if (tab === 'skills') {
            return (
                <div><Card className="padded-card"><h2 className="text-lg font-bold">Technical Skills</h2><p>Languages/Tools:</p><ul><li>Kotlin, Swift, Python, C#, SQL, JavaScript, AWS, NodeJS, Ansible, Git, React, TypeScript</li></ul><p>Frameworks:</p><ul><li>Jetpack Compose, SwiftUI, ASP.NET Core, Laravel</li></ul></Card></div>
            );
        }
        if (tab === 'education') {
            return (
                <div><Card className="padded-card"><h2 className="text-lg font-bold">Georgia Institute of Technology</h2><p>MS Computer Science (Jan 2024 - Present)</p><ul><li>• Modules: Machine Learning for Trading, Operating Systems</li></ul></Card><Card className="padded-card"><h2 className="text-lg font-bold">Durham University</h2><p>BSc Mathematics (Completed Jun 2023)</p></Card></div>
            );
        }
        if (tab === 'about') {
            return (
                <div><Card className="padded-card"><p>
                            I'm Qasam Ahmad, a Software Development Engineer at AWS with a strong foundation in mathematics and computer science. My journey includes developing machine learning models at TikTok and contributing to impactful projects at Amazon and Expedia. I focus on building efficient, scalable solutions that address real-world challenges.
                        </p><p>
                            Alongside my work in software development, I'm passionate about entrepreneurship and exploring new opportunities to innovate. I believe in the power of technology to drive change and am always eager to collaborate on ideas that push boundaries.
                        </p><p>
                            If you share similar interests or have an exciting project in mind, feel free to reach out—I'm always open to connecting with like-minded individuals.
                        </p><h3 className="text-lg font-bold mt-4">Contact Information:</h3><ul><li><a href="mailto:qasam.work@gmail.com" className="flex items-center text-blue-400"><EnvelopeClosedIcon className="mr-2" /> qasam.work@gmail.com
                                </a></li><li><a href="https://www.linkedin.com/in/qa1/" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-400"><LinkedInLogoIcon className="mr-2" /> LinkedIn Profile
                                </a></li></ul></Card></div>
            );
        }
        return null;
    };

    const closeMenu = () => {
        if (menuOpen) {
            setMenuOpen(false);
        }
    };

    const particlesOptions = useMemo(
        () => ({
            background: {
                color: {
                    value: "transparent",
                },
            },
            fpsLimit: 120,
            interactivity: {
                events: {
                    onClick: {
                        enable: true,
                        mode: "push",
                    },
                    onHover: {
                        enable: true,
                        mode: "grab", // This makes nodes link on hover
                    },
                },
                modes: {
                    push: {
                        quantity: 4,
                    },
                    grab: {
                        distance: 140,
                        links: {
                            opacity: 0.6,
                        },
                    },
                },
            },
            particles: {
                color: {
                    value: ["#ff0000", "#00ff00", "#0000ff", "#ffff00", "#ff00ff", "#00ffff"],
                },
                links: {
                    color: "#ffffff",
                    distance: 150,
                    enable: true,
                    opacity: 0.5,
                    width: 1,
                },
                move: {
                    direction: "none",
                    enable: true,
                    outModes: {
                        default: "bounce",
                    },
                    random: false,
                    speed: 3,
                    straight: false,
                },
                number: {
                    density: {
                        enable: true,
                        area: 800,
                    },
                    value: 120,
                },
                opacity: {
                    value: 0.7,
                },
                shape: {
                    type: "circle",
                },
                size: {
                    value: { min: 1, max: 5 },
                },
            },
            detectRetina: true,
        }),
        []
    );    

    return (
        <div className="app-container">
            {init && (
                <Particles id="tsparticles" options={particlesOptions} />
            )}
    
            <div className="top-bar fixed top-0 left-0 w-full z-30 shadow-md flex justify-between items-center p-2"><div className="flex items-center gap-2"><Button onClick={() => { setTab('about'); setView('detailed'); }} className="text-xl font-bold md:text-2xl monospaced-font">
                        <span>QASAM AHMAD</span></Button><Button 
                        onClick={() => { setView(view === 'console' ? 'detailed' : 'console'); closeMenu(); }} 
                        className={`text-blue-400 border-blue-400 ${view === 'console' ? 'text-green-400' : ''}`}
                    >
                        <CodeIcon className="w-6 h-6" /></Button></div>
                {view === 'detailed' && (
                    <Button onClick={() => setMenuOpen(!menuOpen)} className="ml-auto">
                        <HamburgerMenuIcon className="w-6 h-6" /></Button>
                )}
            </div><div className="content-wrapper">
                {menuOpen && (
                    <div className="vertical-menu p-2 z-40"><Button 
                            onClick={() => { setTab('experience'); closeMenu(); }}
                            className={tab === 'experience' ? 'text-green-400' : 'text-blue-400'}
                        >
                            Experience
                        </Button><Button 
                            onClick={() => { setTab('skills'); closeMenu(); }}
                            className={tab === 'skills' ? 'text-green-400' : 'text-blue-400'}
                        >
                            Skills
                        </Button><Button 
                            onClick={() => { setTab('education'); closeMenu(); }}
                            className={tab === 'education' ? 'text-green-400' : 'text-blue-400'}
                        >
                            Education
                        </Button><Button 
                            onClick={() => { setTab('about'); closeMenu(); }}
                            className={tab === 'about' ? 'text-green-400' : 'text-blue-400'}
                        >
                            About Me
                        </Button></div>
                )}
                {view === 'console' ? (
                    <div className="terminal-wrapper"><Terminal 
                            colorMode={ColorMode.Dark} 
                            onInput={handleInput} 
                            className="terminal" 
                            height={terminalHeight} 
                            redBtnCallback={() => setView('detailed')}
                        >
                            {terminalLineData}
                        </Terminal></div>
                ) : (
                    <div className="detailed-view"><h1 className="text-2xl font-bold mb-4 mt-2">
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </h1><div className="tab-content-wrapper">
                            {renderContent()}
                        </div></div>
                )}
            </div></div>
    );    
};

export default App;